import { useState } from 'react';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '@/components/ui/button';
import { base44 } from '@/api/base44Client';
import AddressAutocomplete from '../components/AddressAutocomplete';

const saunaModels = [
  { id: 'ly', name: 'LYKKE LY' },
  { id: 'panorama', name: 'LYKKE PANORAMA' },
  { id: 'fjel', name: 'LYKKE FJELL' },
];

const colors = [
  { id: 'anthracite', name: 'Antrasite' },
  { id: 'dark-grey', name: 'Mørk grå' },
  { id: 'light-grey', name: 'Lys grå' },
  { id: 'natural', name: 'Naturlig tre' },
];

const addOns = [
  { id: 'wifi', name: 'WiFi-kontroll' },
  { id: 'extended-warranty', name: 'Utvidet garanti (5 år)' },
  { id: 'installation', name: 'Installasjonstjeneste' },
  { id: 'maintenance-kit', name: 'Vedlikeholdspakke' },
];

export default function RequestQuote() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    city: '',
    saunaModel: '',
    color: '',
    addOns: [],
    message: '',
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddOnChange = (addonId) => {
    setFormData(prev => ({
      ...prev,
      addOns: prev.addOns.includes(addonId)
        ? prev.addOns.filter(id => id !== addonId)
        : [...prev.addOns, addonId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await base44.integrations.Core.SendEmail({
        to: 'kontakt@lykke.info',
        subject: `Ny tilbudsforespørsel fra ${formData.firstName} ${formData.lastName}`,
        body: `
Navn: ${formData.firstName} ${formData.lastName}
Email: ${formData.email}
Telefon: ${formData.phone}
By: ${formData.city}

Badstuemodell: ${saunaModels.find(m => m.id === formData.saunaModel)?.name}
Farge: ${colors.find(c => c.id === formData.color)?.name}
Tilleggsutstyr: ${formData.addOns.length > 0 ? formData.addOns.map(id => addOns.find(a => a.id === id)?.name).join(', ') : 'Ingen'}

Melding:
${formData.message}
        `
      });

      setSuccess(true);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        city: '',
        saunaModel: '',
        color: '',
        addOns: [],
        message: '',
      });

      setTimeout(() => setSuccess(false), 5000);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="pt-32 md:pt-40 pb-24 md:pb-40">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-16"
          >
            <p className="text-sm tracking-widest-2xl font-body uppercase text-primary mb-6">
              Be om tilbud
            </p>
            <h1 className="font-heading text-4xl md:text-5xl font-light leading-[1.2] mb-6 text-[#2a1810]">
              Få ditt
              <br />
              <span className="italic">saunatilbud</span>
            </h1>
            <p className="text-foreground/70 text-lg font-body max-w-xl">
              Fyll ut skjemaet nedenfor og vi sender deg et personlig tilbud basert på dine valg.
            </p>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            onSubmit={handleSubmit}
            className="bg-secondary/50 rounded-[6px] p-8 md:p-12"
          >
            {/* Personal Info */}
            <div className="mb-12">
              <h3 className="font-heading text-xl font-light mb-6 text-[#2a1810]">Kontaktinformasjon</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-body mb-2 text-foreground/70">Fornavn *</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-white border border-border rounded-[4px] font-body focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
                <div>
                  <label className="block text-sm font-body mb-2 text-foreground/70">Etternavn *</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-white border border-border rounded-[4px] font-body focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
                <div>
                  <label className="block text-sm font-body mb-2 text-foreground/70">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-white border border-border rounded-[4px] font-body focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
                <div>
                  <label className="block text-sm font-body mb-2 text-foreground/70">Telefon *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-white border border-border rounded-[4px] font-body focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
                <div>
                  <label className="block text-sm font-body mb-2 text-foreground/70">Adresse *</label>
                  <AddressAutocomplete
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    required
                    placeholder="Skriv inn adressen din..."
                    className="w-full px-4 py-3 bg-white border border-border rounded-[4px] font-body focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
              </div>
            </div>

            {/* Sauna Configuration */}
            <div className="mb-12">
              <h3 className="font-heading text-xl font-light mb-6 text-[#2a1810]">Velg din badstue</h3>
              
              <div className="mb-8">
                <label className="block text-sm font-body mb-4 text-foreground/70">Badstuemodell *</label>
                <div className="space-y-3">
                  {saunaModels.map(model => (
                    <label key={model.id} className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="saunaModel"
                        value={model.id}
                        checked={formData.saunaModel === model.id}
                        onChange={handleInputChange}
                        required
                        className="w-4 h-4"
                      />
                      <span className="font-body text-foreground">{model.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-body mb-4 text-foreground/70">Farge *</label>
                <div className="space-y-3">
                  {colors.map(color => (
                    <label key={color.id} className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="color"
                        value={color.id}
                        checked={formData.color === color.id}
                        onChange={handleInputChange}
                        required
                        className="w-4 h-4"
                      />
                      <span className="font-body text-foreground">{color.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Add-ons */}
            <div className="mb-12">
              <h3 className="font-heading text-xl font-light mb-6 text-[#2a1810]">Tilleggsutstyr (valgfritt)</h3>
              <div className="space-y-3">
                {addOns.map(addon => (
                  <label key={addon.id} className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.addOns.includes(addon.id)}
                      onChange={() => handleAddOnChange(addon.id)}
                      className="w-4 h-4"
                    />
                    <span className="font-body text-foreground">{addon.name}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Message */}
            <div className="mb-12">
              <label className="block text-sm font-body mb-2 text-foreground/70">Ekstra info eller spørsmål</label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                rows="5"
                className="w-full px-4 py-3 bg-white border border-border rounded-[4px] font-body focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                placeholder="Fortell oss mer om prosjektet ditt..."
              />
            </div>

            {success && (
              <div className="mb-6 p-4 bg-green-100/50 border border-green-300 rounded-[4px] text-green-800 font-body">
                Takk! Vi har mottatt din forespørsel og kontakter deg snart.
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#2a1810] hover:bg-[#2a1810]/90 text-white font-body tracking-widest text-sm py-3 rounded-[4px]"
            >
              {loading ? 'Sender...' : 'Be om tilbud'}
            </Button>
          </motion.form>
        </div>
      </div>

      <Footer />
    </div>
  );
}